import React from "react";
import { useQuery, useApolloClient, useSubscription } from "@apollo/client";
import { gql } from "@apollo/client";

import { Box, LinearProgress, Grid, Fab } from "@mui/material";
import { Plus as CreateIcon } from "mdi-material-ui";

import CreateOrderDialog from "components/order/CreateOrderDialog";
import HiddenStates, { useHiddenStates } from "components/order/HiddenStates";
import MoveAllOrdersDialog from "components/order/MoveAllOrdersDialog";
import OrderState from "components/order/OrdersListState";
import Layout from "components/common/Layout";
import SearchInput from "components/common/SearchInput";

// Poor man's deep clone, we're using it only for graphql data,
// in this case it's ok to use JSON serialize/deserialize to clone
// object.
const deepClone = (val) => JSON.parse(JSON.stringify(val));

const QUERY = gql`
  query ($exclude: [ID], $search: String) {
    orderStates(exclude: $exclude) {
      id

      orders(first: 20, search: $search) {
        count
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            priority
          }
        }

        ...OrdersListState_orderConnection
      }

      ...HiddenStates_orderState
      ...MoveAllOrdersDialog_orderState
      ...OrdersListState_orderState
    }
  }

  ${HiddenStates.fragments.orderState}
  ${MoveAllOrdersDialog.fragments.orderState}
  ${OrderState.fragments.orderState}
  ${OrderState.fragments.orderConnection}
`;

const LOAD_NEXT_QUERY = gql`
  query ($orderStateId: ID!, $after: String!, $search: String) {
    orderState(id: $orderStateId) {
      id

      orders(first: 20, after: $after, search: $search) {
        count
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            priority
          }
        }

        ...OrdersListState_orderConnection
      }
    }
  }

  ${OrderState.fragments.orderConnection}
`;

const SUBSCRIPTION = gql`
  subscription ($search: String) {
    orderUpdates(search: $search) {
      order {
        id
        priority

        state {
          id
        }

        ...OrdersListState_order
      }
      stateChanged
      prevStateId
    }
  }

  ${OrderState.fragments.order}
`;

function updateOrderState(client, orderState, search) {
  client.writeFragment({
    id: `OrderState:${orderState.id}`,
    fragmentName: "UpdateOrderState",
    variables: { search },
    fragment: gql`
      fragment UpdateOrderState on OrderState {
        orders(first: 20, search: $search) {
          count
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              priority
            }
          }

          ...OrdersListState_orderConnection
        }
      }

      ${OrderState.fragments.orderConnection}
    `,
    data: orderState,
  });
}

function addOrderToState(client, order, orderState, search, incCount) {
  const orderEdge = {
    node: order,
    __typename: "OrderEdge",
  };

  orderState = deepClone(orderState);
  orderState.orders.edges = orderState.orders.edges.filter(
    (edge) => edge.node.id !== order.id
  );
  if (incCount) orderState.orders.count += 1;

  if (orderState.orders.edges.length === 0) {
    orderState.orders.edges.push(orderEdge);
  } else {
    const firstEdge = orderState.orders.edges[0];
    const lastEdge =
      orderState.orders.edges[orderState.orders.edges.length - 1];

    if (order.priority < firstEdge.node.priority) {
      orderState.orders.edges.unshift(orderEdge);
    } else if (order.priority > lastEdge.node.priority) {
      if (!orderState.orders.pageInfo.hasNextPage) {
        orderState.orders.edges.push(orderEdge);
      }
    } else {
      const index = orderState.orders.edges.findIndex(
        ({ node }) => node.priority > order.priority
      );
      orderState.orders.edges.splice(index, 0, orderEdge);
    }
  }

  updateOrderState(client, orderState, search);
}

function removeOrderFromState(client, order, orderState, search, decCount) {
  orderState = deepClone(orderState);
  if (decCount) orderState.orders.count -= 1;

  const index = orderState.orders.edges.findIndex(
    ({ node }) => node.id === order.id
  );
  if (index >= 0) orderState.orders.edges.splice(index, 1);

  updateOrderState(client, orderState, search);
}

export default function OrderPage() {
  const apolloClient = useApolloClient();

  const [search, setSearch] = React.useState("");

  const { hiddenStates, hideState } = useHiddenStates();

  const [moveAllOrdersFrom, setMoveAllOrdersFrom] = React.useState(null);

  const exclude = search ? [] : hiddenStates.map((s) => s.id);
  exclude.push("59c37ddd6bd5b0ea037b5a8f"); // deleted

  const { data, loading } = useQuery(QUERY, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: {
      exclude,
      search,
    },
  });

  // Wrap graphql data in a ref so we can use it in the following
  // callbacks without invalidating the callback themselves, this way
  // the memoized version of OrderState component can work properly
  // while still skipping as many re-render as possible
  const dataRef = React.useRef(null);
  React.useEffect(() => {
    dataRef.current = data;
  }, [data, apolloClient]);

  useSubscription(SUBSCRIPTION, {
    variables: { search },
    onSubscriptionData: ({ client, subscriptionData }) => {
      const { order, stateChanged, prevStateId } =
        subscriptionData.data.orderUpdates;

      for (const orderState of dataRef.current?.orderStates ?? []) {
        if (orderState.id === prevStateId)
          removeOrderFromState(client, order, orderState, search, stateChanged);

        if (orderState.id === order.state.id)
          addOrderToState(client, order, orderState, search, stateChanged);
      }
    },
  });

  const handleOrderDrop = React.useCallback(
    (order, prevStateId, newStateId) => {
      for (const orderState of dataRef.current?.orderStates ?? []) {
        if (orderState.id === prevStateId)
          removeOrderFromState(apolloClient, order, orderState, search, false);

        if (orderState.id === newStateId)
          addOrderToState(apolloClient, order, orderState, search, false);
      }
    },
    [apolloClient, search]
  );

  const handleOrderDropRollback = React.useCallback(
    (err, order, prevStateId, newStateId) => {
      console.error(err);

      for (const orderState of dataRef.current?.orderStates ?? []) {
        if (orderState.id === prevStateId)
          addOrderToState(apolloClient, order, orderState, search, false);

        if (orderState.id === newStateId)
          removeOrderFromState(apolloClient, order, orderState, search, false);
      }
    },
    [apolloClient, search]
  );

  const handleLoadNext = React.useCallback(
    async (orderState) => {
      await apolloClient.query({
        query: LOAD_NEXT_QUERY,
        fetchPolicy: "network-only",
        variables: {
          orderStateId: orderState.id,
          after: orderState.orders.pageInfo.endCursor,
          search,
        },
      });
    },
    [apolloClient, search]
  );

  return (
    <>
      <Layout.Toolbar maxWidth={false}>
        <Grid container alignItems="center">
          <Grid item xs>
            <SearchInput onChange={setSearch} />
          </Grid>
          <Grid item>
            <CreateOrderDialog>
              {({ open }) => (
                <Fab onClick={open} color="primary" size="small">
                  <CreateIcon />
                </Fab>
              )}
            </CreateOrderDialog>
          </Grid>
          {Boolean(hiddenStates.length) && (
            <Grid item xs={12}>
              <HiddenStates />
            </Grid>
          )}
        </Grid>
      </Layout.Toolbar>

      {loading && <LinearProgress />}

      <MoveAllOrdersDialog
        fromOrderState={moveAllOrdersFrom}
        onClose={() => setMoveAllOrdersFrom(null)}
      />

      <Box
        sx={{ flexGrow: 1, flexBasis: 0, display: "flex", overflow: "auto" }}
      >
        {data?.orderStates.map((orderState) => {
          if (!!search && orderState.orders.edges.length === 0) return null;

          return (
            <OrderState
              key={orderState.id}
              orderState={orderState}
              onStateHide={hideState}
              onMoveAllOrders={setMoveAllOrdersFrom}
              onOrderDrop={handleOrderDrop}
              onOrderDropRollback={handleOrderDropRollback}
              loadNext={handleLoadNext}
            />
          );
        })}
      </Box>
    </>
  );
}
